import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { useManagedLocations } from '../../auth/useManagedLocations';
import {
  ClientDirectoryClient,
  ClientDirectoryRole,
  ClientDirectoryService,
  ClientDirectoryUserContext,
} from '../services/clientDirectoryService';

type UseClientDirectoryState = {
  clients: ClientDirectoryClient[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

export const useClientDirectory = (options?: { enabled?: boolean }): UseClientDirectoryState => {
  const enabled = options?.enabled ?? true;
  const { user, userRole, userMetadata } = useAuth();
  const assignedLocations = useManagedLocations();

  const ctx = useMemo<ClientDirectoryUserContext | null>(() => {
    if (!userRole) return null;
    if (userRole !== 'ADMIN' && userRole !== 'MANAGER' && userRole !== 'USER') return null;

    return {
      userId: user?.uid,
      userRole: userRole as ClientDirectoryRole,
      coachId: userMetadata?.coach_id,
      assignedLocations,
    };
  }, [user?.uid, assignedLocations, userMetadata?.coach_id, userRole]);

  const cacheKey = useMemo(() => (ctx ? ClientDirectoryService.getCacheKey(ctx) : null), [ctx]);

  const [clients, setClients] = useState<ClientDirectoryClient[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!ctx) return;
    setLoading(true);
    setError(null);
    try {
      const fresh = await ClientDirectoryService.getClients(ctx, { forceRefresh: true });
      setClients(fresh);
    } catch (error) {
      console.error('Failed to refresh client directory', error);
      setError('Failed to refresh clients');
    } finally {
      setLoading(false);
    }
  }, [ctx]);

  useEffect(() => {
    if (!enabled) return;
    if (!ctx) return;

    const cached = ClientDirectoryService.readCache(ctx);
    if (cached) {
      setClients(cached.clients);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    ClientDirectoryService.getClients(ctx)
      .then(fetched => {
        if (cancelled) return;
        setClients(fetched);
      })
      .catch(error => {
        console.error('Failed to load client directory', error);
        if (cancelled) return;
        setError('Failed to load clients');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, ctx, enabled]);

  return { clients, loading, error, refresh };
};

